import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  PermissionObj,
  RoleObj,
  PaginationParams,
  GetInvoicesByCustomerIdPayloadType,
  Invoice,
  UpdatePracticeDetailsPayloadType,
  PracticeData,
  importPlansFromSheetPayloadType,
  ImportInternalPlansPayloadType,
  EnablePracticeLocationPayloadType,
  ToggleIsSheetLockPayloadType,
  fetchUserDetailsPayloadType
} from './interfaces';

import * as _ from 'lodash';

import {
  ACTION_NAMES,
  DATE_FORMATS,
  INVOICES_FREQUENCY,
  SLICES,
  TOAST_MSGS
} from './../../constants/index';
import {
  checkSubscriptionExpiry,
  createResetAction,
  isTrialUser,
  retrieveDataFromLocalStorage,
  getChangedKeys,
  sortData,
  formatDate,
  sortSearchedData
} from '../../utils';
import axiosInstance from '../../axiosInstance';
import { setShowAlert } from '../utilts/utilsSlice';
import {
  UsersBillingAddressParamsType,
  UserState,
  UserPaginationMeta,
  UpdateRole,
  UpdatePermission,
  CreatePermissionsParamsType,
  UpdateUserDataParamsType,
  AccessToken
} from './interfaces';
import { RootState, StoreMethods } from '../store';
import { updateLocalStorage } from './../../utils/index';
import {
  createRoleService,
  createTrialUserService,
  deleteTrialUser,
  deleteUsersActiveSession,
  fetchUsersService,
  getInvoicesByCustomerId,
  getNextInvoiceDateByCustomerId,
  updateUser,
  updatePractice,
  importPlans,
  importCDTCodes,
  importInternalPlans,
  enablePracticeLocation,
  toggleIsSheetLock,
  updateUserByAdmin
} from '../services/user/api';
import {
  CreateRoleParamsType,
  DeleteTrialUser,
  FetchUsers,
  UpdateTrialUser
} from '../services/user/types';
import { TOAST_TYPE } from './../../utils/constantTypes';
import { importCodeToPlanAsync } from '../subscriptions/subscriptionSlice';
import { resetAuth } from '../auth/authSlice';
import { FetchPracticeById } from '../services/practice/types';
import { fetchPracticeDetailsId } from '../services/practice/api';

const initialSubscriptionData = {
  pause: {
    isPaused: false,
    pausedAt: null,
    resumeAt: null,
    source: null,
    updatedBy: null
  },
  _id: '',
  tier: '',
  createdAt: '',
  expiredAt: '',
  productId: '',
  description: '',
  paymentStatus: null,
  subscriptionId: null,
  creditCardRequired: false,
  freeTrialUsed: false,
  isCancelled: false,
  hasExpired: false,
  nextInvoiceDate: null
};

const initialPaymentData = {
  cardHolderName: '',
  type: '',
  number: '',
  customerId: '',
  city: '',
  country: '',
  state: '',
  zipcode: '',
  addLine1: '',
  addLine2: ''
};

const initialPracticeData = {
  name: '',
  state: '',
  city: '',
  zipcode: '',
  addLine1: '',
  addLine2: '',
  suite: '',
  officePhoneNumber: { countryCode: '', number: '' },
  practiceCommonEmail: ''
};

const defaultPagination: UserPaginationMeta = {
  offset: 0,
  limit: 10,
  totalItems: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false
};

const initialState: UserState = {
  userData: {
    firstName: '',
    lastName: '',
    userRole: '',
    fullName: '',
    email: '',
    role: '',
    title: '',
    phoneNumber: { countryCode: '', number: '' },
    licenseNumber: '',
    notificationSetting: {
      email: null,
      sms: null,
      push: null
    },
    calendarSetting: {
      operatories: 0,
      internals: 0
    },
    licenseState: '',
    managementSoftware: '',
    avatar: '',
    userId: '',
    practiceId: '',
    practice: initialPracticeData,
    subscription: initialSubscriptionData,
    agreementAccepted: true,
    payment: initialPaymentData,
    officeId: '',
    practiceLocationEnabled: null,
    subscriptionDate: null,
    discountPrice: null,
    duration: null,
    durationInMonths: null,
    couponCode: null,
    subscriptionPrice: null,
    isSikkaSetupEnabled: null,
    invoices: [],
    userSetting: {
      sendReportToCommonEmail: null,
      reportSummary: ''
    },
    isActive: null,
    googleSheet: {
      spreadsheetId: null,
      spreadsheetUrl: null,
      importStatus: null,
      isLock: true
    },
    internalCodeGoogleSheet: {
      spreadsheetId: null,
      spreadsheetUrl: null,
      importStatus: null,
      isLock: true
    },
    additionalPhoneNumber: null,
    additionalEmails: null
  },
  designation: '',
  loading: false,
  error: null,
  currentRole: '',
  roles: [],
  permissions: [],
  searchedUsers: [],
  selectedUser: {},
  offset: 0,
  limit: 10,
  sortBy: '',
  sortField: '',
  pagination: defaultPagination,
  sortDirection: 'asc'
};

const userSlice = createSlice({
  name: 'User',
  initialState,
  reducers: {
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },
    resetUser: createResetAction(initialState),
    setLoader: (state, action) => {
      state.loading = action.payload;
    },
    setPagination: (state, action: PayloadAction<PaginationParams>) => {
      state.limit = action.payload.limit;
      state.offset = action.payload.offset;
      state.pagination = {
        ...state.pagination,
        offset: action.payload.offset,
        limit: action.payload.limit
      };
    },
    resetSearchedUsers: (state) => {
      state.searchedUsers = [];
    },
    updateUserData: (state, action) => {
      state.userData = { ...state.userData, ...action.payload };
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setSortField: (state, action: PayloadAction<string>) => {
      state.sortField = action.payload;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },
    setSearchedUsers: (state, action: PayloadAction<string>) => {
      const sortedData = sortSearchedData(
        state.searchedUsers,
        action.payload,
        state.sortDirection
      );
      state.searchedUsers = sortedData;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllRolesAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      fetchAllRolesAsync.fulfilled,
      (state, action: PayloadAction<RoleObj[]>) => {
        state.loading = false;
        state.roles = action.payload;
      }
    );
    builder.addCase(fetchAllRolesAsync.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(fetchAllPermissionsAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      fetchAllPermissionsAsync.fulfilled,
      (state, action: PayloadAction<PermissionObj[]>) => {
        state.loading = false;
        state.permissions = action.payload;
      }
    );
    builder.addCase(fetchAllPermissionsAsync.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(
      createRoleAsync.fulfilled,
      (state, action: PayloadAction<RoleObj>) => {
        state.loading = false;
        state.roles = [...state.roles, { ...action.payload }];
      }
    );
    builder.addCase(createRoleAsync.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(createPermissionAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      createPermissionAsync.fulfilled,
      (state, action: PayloadAction<PermissionObj>) => {
        state.loading = false;
        state.permissions = [...state.permissions, { ...action.payload }];
      }
    );

    builder.addCase(createPermissionAsync.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(fetchUserDetailsAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchUserDetailsAsync.fulfilled, (state, action) => {
      state.loading = false;
      const payment = _.get(action.payload, 'payment', {});
      _.omit(action.payload, 'payment');
      const subscription = _.get(action.payload, 'subscription', {});
      const pause = _.get(subscription, 'pause', {});
      const hasIncomingPause =
        _.has(subscription, 'pause') || _.has(subscription, 'pauseCollection');
      const previousPause = _.get(state.userData, 'subscription.pause', {});
      const previousPauseCollection = _.get(
        state.userData,
        'subscription.pauseCollection',
        null
      );
      state.userData = {
        ...state.userData,
        ...action.payload,
        subscription: {
          ...initialSubscriptionData,
          ...subscription,
          pauseCollection: hasIncomingPause
            ? _.get(subscription, 'pauseCollection', null)
            : previousPauseCollection,
          pause: {
            ...(hasIncomingPause ? initialSubscriptionData.pause : previousPause),
            ...pause
          }
        },
        payment: { ...state.userData.payment, ...payment }
      };
    });
    builder.addCase(fetchUserDetailsAsync.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(updateRoleAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateRoleAsync.fulfilled, (state, action) => {
      state.loading = false;
      const index = _.findIndex(state.roles, { id: action.payload.id });
      const newArray = _.cloneDeep(state.roles);
      if (index !== -1) {
        newArray[index] = action.payload;
      }
      state.roles = newArray;
    });
    builder.addCase(updateRoleAsync.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(updatePermissionAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updatePermissionAsync.fulfilled, (state, action) => {
      state.loading = false;
      const index = _.findIndex(state.permissions, { id: action.payload.id });
      const newArray = _.cloneDeep(state.permissions);
      if (index !== -1) {
        newArray[index] = action.payload;
      }
      state.permissions = newArray;
    });
    builder.addCase(updatePermissionAsync.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(updateUserAsync.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateUserAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.userData.notificationSetting = action.payload;
    });
    builder.addCase(updateUserAsync.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(updateUserByAdminAsync.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateUserByAdminAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(updateUserByAdminAsync.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(getUsersBillingAddressAsync.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getUsersBillingAddressAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.userData.payment = { ...state.userData.payment, ...action.payload };
    });
    builder.addCase(getUsersBillingAddressAsync.rejected, (state, action) => {
      state.loading = false;
    });
    // builder.addCase(checkPracticeLocationStatusAsync.pending, (state) => {
    //   state.loading = true;
    //   state.error = null;
    // });
    // builder.addCase(
    //   checkPracticeLocationStatusAsync.fulfilled,
    //   (state, action) => {
    //     state.loading = false;
    //     state.userData.practiceLocationEnabled = action.payload;
    //   }
    // );
    // builder.addCase(
    //   checkPracticeLocationStatusAsync.rejected,
    //   (state, action) => {
    //     state.loading = false;
    //   }
    // );
    builder.addCase(createTrialUserAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createTrialUserAsync.fulfilled, (state) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(createTrialUserAsync.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(fetchUsersByConditionAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchUsersByConditionAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.searchedUsers = action.payload.data;
      state.pagination = action.payload.pagination;
      state.limit = action.payload.pagination.limit;
      state.offset = action.payload.pagination.offset;
    });
    builder.addCase(fetchUsersByConditionAsync.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteTrialUserAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteTrialUserAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.searchedUsers = action.payload;
    });
    builder.addCase(deleteTrialUserAsync.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(updateTrialUserAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateTrialUserAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(updateTrialUserAsync.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(fetchInvoicesByCustomerIdAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      fetchInvoicesByCustomerIdAsync.fulfilled,
      (state, action) => {
        state.loading = false;
        state.error = null;
        state.userData.invoices = action.payload;
      }
    );
    builder.addCase(fetchInvoicesByCustomerIdAsync.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(fetchNextInvoiceDateByCustomerIdAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      fetchNextInvoiceDateByCustomerIdAsync.fulfilled,
      (state, action) => {
        state.loading = false;
        state.error = null;
        state.userData.subscription.nextInvoiceDate = action.payload;
      }
    );
    builder.addCase(fetchNextInvoiceDateByCustomerIdAsync.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(updateEmailAndPhoneNumber.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateEmailAndPhoneNumber.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(updateEmailAndPhoneNumber.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteUsersAllActiveSessions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteUsersAllActiveSessions.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(deleteUsersAllActiveSessions.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(updatePracticeAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updatePracticeAsync.fulfilled, (state, action) => {
      const sendReportToCommonEmail = _.get(
        action.payload,
        'sendReportToCommonEmail',
        state.userData.userSetting.sendReportToCommonEmail
      );
      _.omit(action.payload, 'sendReportToCommonEmail');
      state.loading = false;
      state.error = null;
      state.userData.practice = {
        ...state.userData.practice,
        ...action.payload,
        officePhoneNumber: {
          ...state.userData.practice.officePhoneNumber,
          ...action.payload.officePhoneNumber
        }
      };
      state.userData.userSetting.sendReportToCommonEmail =
        sendReportToCommonEmail;
    });
    builder.addCase(updatePracticeAsync.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(fetchPracticeByIdAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchPracticeByIdAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      const sendReportToCommonEmail = _.get(
        action.payload,
        'sendReportToCommonEmail',
        state.userData.userSetting.sendReportToCommonEmail
      );
      _.omit(action.payload, 'sendReportToCommonEmail');
      state.userData.practice = {
        ...state.userData.practice,
        ...action.payload
      };
      state.userData.userSetting.sendReportToCommonEmail =
        sendReportToCommonEmail;
    });
    builder.addCase(fetchPracticeByIdAsync.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(importPlansFromSheetAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(importPlansFromSheetAsync.fulfilled, (state, action) => {
      state.loading = false;
    });
    builder.addCase(importPlansFromSheetAsync.rejected, (state) => {
      state.loading = false;
    });
    // builder.addCase(importInternalPlansAsync.pending, (state) => {
    //   state.loading = true;
    // });
    // builder.addCase(importInternalPlansAsync.fulfilled, (state, action) => {
    //   state.loading = false;
    // });
    // builder.addCase(importInternalPlansAsync.rejected, (state) => {
    //   state.loading = false;
    // });
    builder.addCase(enablePracticeLocationAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(enablePracticeLocationAsync.fulfilled, (state, action) => {
      state.loading = false;
    });
    builder.addCase(enablePracticeLocationAsync.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(toggleIsSheetLockAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(toggleIsSheetLockAsync.fulfilled, (state, action) => {
      state.loading = false;
    });
    builder.addCase(toggleIsSheetLockAsync.rejected, (state) => {
      state.loading = false;
    });
  }
});

export const fetchAllRolesAsync = createAsyncThunk(
  'user/fetchAllRoles',
  async (payload: AccessToken, { rejectWithValue }) => {
    try {
      const rolesRes = await axiosInstance.get('role/all', {
        headers: {
          Authorization: `Bearer ${payload.accessToken}`
        }
      });
      const rolesData = _.get(rolesRes, 'data.data');
      const roles = _.chain(rolesData)
        .sortBy('createdAt')
        .map((role) => ({
          id: role._id,
          name: role.name,
          description: role.description
        }))
        .value();
      return roles;
    } catch (err) {
      console.log('ERROR', err);
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const fetchAllPermissionsAsync = createAsyncThunk(
  'user/fetchAllPermisiions',
  async (payload: AccessToken, { rejectWithValue }) => {
    try {
      const permissionsRes = await axiosInstance.get('permission/all', {
        headers: {
          Authorization: `Bearer ${payload.accessToken}`
        }
      });
      const permissionsData = _.get(permissionsRes, 'data.data');
      const permissions = _.chain(permissionsData)
        .sortBy('createdAt')
        .map((permission) => ({
          id: permission._id,
          name: permission.name,
          description: permission.description
        }))
        .value();
      return permissions;
    } catch (err) {
      console.log('ERROR', err);
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const createRoleAsync = createAsyncThunk(
  `${SLICES.USER}/${ACTION_NAMES.CREATE_ROLE}`,
  async (payload: CreateRoleParamsType, { dispatch, rejectWithValue }) => {
    try {
      const rolesData = await createRoleService(payload, { dispatch });
      dispatch(
        setShowAlert({
          show: true,
          message: rolesData.msg,
          type: TOAST_TYPE.SUCCESS
        })
      );
      return rolesData.data;
    } catch (err) {
      console.log('Error', err);
      return rejectWithValue(err?.message);
    }
  }
);

export const createPermissionAsync = createAsyncThunk(
  'user/CreatePermission',
  async (
    payload: CreatePermissionsParamsType,
    { dispatch, rejectWithValue }
  ) => {
    try {
      const _payload = _.omit(payload, 'accessToken');
      const permissionRes = await axiosInstance.post('permission', _payload, {
        headers: {
          Authorization: `Bearer ${payload.accessToken}`
        }
      });
      const permissionId = _.get(permissionRes, 'data.data.id', '');
      const msg = _.get(permissionRes, 'data.msg');
      dispatch(
        setShowAlert({
          show: true,
          message: msg,
          type: TOAST_TYPE.SUCCESS
        })
      );
      return { id: permissionId, ..._payload };
    } catch (err) {
      if (err?.response?.data.statusCode !== 401) {
        dispatch(
          setShowAlert({
            show: true,
            message: err?.response?.data?.msg || err?.response?.data?.message,
            type: 'error'
          })
        );
      }
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const fetchUserDetailsAsync = createAsyncThunk(
  'auth/fetchUserDetails',
  async (
    payload: fetchUserDetailsPayloadType,
    { dispatch, rejectWithValue, getState }
  ) => {
    try {
      const userRes = await axiosInstance.get('user/byId', {
        headers: {
          Authorization: `Bearer ${payload.accessToken}`
        }
      });
      const userData = _.get(userRes, 'data.data', {});
      const localRole = retrieveDataFromLocalStorage(['role']);
      const currentRole = _.get(userData, 'role', '');
      if (!localRole || (currentRole && currentRole !== localRole)) {
        updateLocalStorage([{ key: 'role', value: _.get(userData, 'role') }]);
      }
      const userRoleId = _.get(userData, 'roleId');
      let roleData;
      if (userRoleId) {
        const roleRes = await axiosInstance.get(`role/${userRoleId}`, {
          headers: {
            Authorization: `Bearer ${payload.accessToken}`
          }
        });
        roleData = _.get(roleRes, 'data.data');
      }
      const userRole = _.get(roleData, 'name', '');
      const subscriptionRoot = _.get(userData, 'subscription', {});
      const subscriptionData = _.get(userData, 'subscription.active', {
        ...initialSubscriptionData,
        _id: null
      });
      const subscriptionPauseRoot = _.get(subscriptionRoot, 'pause', null);
      const subscriptionPauseCollectionRoot = _.get(
        subscriptionRoot,
        'pauseCollection',
        null
      );
      const expiredAt = _.get(userData, 'subscription.active.expiredAt', null);
      const userSetting = _.get(userData, 'userSetting', {});
      if (expiredAt) {
        const hasExpired = checkSubscriptionExpiry(expiredAt);
        subscriptionData.hasExpired = hasExpired;
      }

      const customerId = _.get(userData, 'paymentInfo.customerId');
      const cardHolderName = _.get(userData, 'paymentInfo.card.name', '');
      const type = _.get(userData, 'paymentInfo.card.type', '');
      const number = _.get(userData, 'paymentInfo.card.number', '');
      const practiceId = _.get(userData, 'practice._id');
      const isSikkaSetupEnabled = _.get(userData, 'isSikkaSetupEnabled');
      const practiceRes = await axiosInstance.get(`practice/${practiceId}`, {
        headers: {
          Authorization: `Bearer ${payload.accessToken}`
        }
      });
      const practiceData = _.get(practiceRes, 'data.data', {});
      const officeId = _.get(practiceData, 'officeId', null);
      const state = _.get(practiceData, 'state', '');
      const name = _.get(practiceData, 'name', '');
      const city = _.get(practiceData, 'city', '');
      const zipcode = _.get(practiceData, 'zipCode', '');
      const addLine1 = _.get(practiceData, 'address1', '');
      const addLine2 = _.get(practiceData, 'address2', '');
      const suite = _.get(practiceData, 'suite', '');
      const officePhoneNumber = _.get(practiceData, 'officePhoneNumber');
      const practiceCommonEmail = _.get(practiceData, 'practiceCommonEmail');
      let practiceLocationEnabled = !!getPracticeLoaction(
        getState() as RootState
      );
      const {
        isLocationEnable = false,
        practiceIds = null,
        userId = null
      } = _.get(payload, 'data', {});

      const hasAllParams = isLocationEnable && practiceIds && userId;

      if (officeId) {
        const practiceLocationRes = await axiosInstance.get(
          `practice/location/status/${officeId}`,
          {
            ...(hasAllParams && {
              params: {
                isLocationEnable,
                practiceIds,
                userId
              }
            }),
            headers: {
              Authorization: `Bearer ${payload.accessToken}`
            }
          }
        );
        const practiceLocationData = _.get(practiceLocationRes, 'data', {});
        const statusCode = _.get(practiceLocationData, 'statusCode');
        const practiceLocationEnabledMsg = _.get(practiceLocationData, 'msg');
        const isProcedureCodeImported = _.get(
          userData,
          'isProcedureCodeImported'
        );
        practiceLocationEnabled =
          statusCode === 200 ? true : practiceLocationEnabled;
        if (
          !isTrialUser(currentRole || localRole) &&
          !isProcedureCodeImported &&
          statusCode === 200 &&
          !localStorage.getItem('importCodes')
        ) {
          // show location enabled toast
          await dispatch(
            setShowAlert({
              show: true,
              message: practiceLocationEnabledMsg,
              type: TOAST_TYPE.SUCCESS
            })
          );
          // // import procedure codes only once when location is turned on 1st time
          let retryCount = 2;
          let hasSuccess = false;
          while (!hasSuccess && retryCount > 0) {
            const importCodeRes = await importCodeToPlanAsync({
              accessToken: payload.accessToken,
              payload: { userId: _.get(userData, '_id'), officeId }
            });
            const statusCode = _.get(importCodeRes, 'data.statusCode');
            if (statusCode === 200) {
              localStorage.setItem('importCodes', 'true');
              // // import CDT Codes only once when location is turned on 1st time
              importCDTCodes(
                {
                  accessToken: payload?.accessToken,
                  data: {
                    userId: _.get(userData, '_id'),
                    officeId
                  }
                },
                { dispatch }
              );
              hasSuccess = true;
            }
            retryCount--;
          }
        }
      }
      const subscriptionDate = _.get(userData, 'subscriptionDate');
      const discountPrice = _.get(userData, 'discountPrice');
      const duration = _.get(userData, 'duration');
      const durationInMonths = _.get(userData, 'durationInMonths');
      const couponCode = _.get(userData, 'couponCode');
      const subscriptionPrice = _.get(userData, 'subscriptionPrice');
      const user = {
        firstName: _.get(userData, 'firstName'),
        lastName: _.get(userData, 'lastName'),
        email: _.get(userData, 'email'),
        fullName: _.get(userData, 'fullName'),
        role: _.get(userData, 'role'),
        title: _.get(userData, 'title'),
        phoneNumber: _.get(userData, 'phoneNumber'),
        licenseNumber: _.get(userData, 'licenseNumber'),
        notificationSetting: _.get(userData, 'notificationSetting'),
        calendarSetting: _.get(userData, 'calendarSetting'),
        licenseState: _.get(userData, 'licenseState'),
        managementSoftware: _.get(userData, 'managementSoftware'),
        userRole,
        userId: _.get(userData, '_id'),
        practiceId,
        practice: {
          name,
          state,
          city,
          zipcode,
          addLine1,
          addLine2,
          suite,
          officePhoneNumber,
          practiceCommonEmail
        },
        subscription: {
          ...subscriptionData,
          pause: {
            ...initialSubscriptionData.pause,
            ...(_.get(subscriptionData, 'pause', {}) || {}),
            ...(subscriptionPauseRoot || {})
          },
          pauseCollection:
            _.get(subscriptionData, 'pauseCollection', null) ||
            subscriptionPauseCollectionRoot ||
            null,
          freeTrialUsed: _.get(userData, 'subscription.freeTrialUsed', null),
          // TODO: change isCanceled to isCancelled from backend
          isCancelled:
            _.get(userData, 'subscription.isCancelled', null) ??
            _.get(userData, 'subscription.isCanceled', null) ??
            false
        },
        agreementAccepted: _.get(userData, 'agreementAccepted'),
        payment: { cardHolderName, type, number, customerId },
        officeId,
        practiceLocationEnabled,
        subscriptionDate,
        discountPrice,
        duration,
        durationInMonths,
        couponCode,
        subscriptionPrice,
        isSikkaSetupEnabled,
        userSetting,
        isActive: _.get(userData, 'isActive', null),
        googleSheet: {
          spreadsheetId: _.get(userData, 'googleSheet.spreadsheetId', null),
          spreadsheetUrl: _.get(userData, 'googleSheet.spreadsheetUrl', null),
          importStatus: _.get(userData, 'googleSheet.importStatus', null),
          isLock: _.get(userData, 'googleSheet.isLock', true)
        },
        internalCodeGoogleSheet: {
          spreadsheetId: _.get(
            userData,
            'internalCodeGoogleSheet.spreadsheetId',
            null
          ),
          spreadsheetUrl: _.get(
            userData,
            'internalCodeGoogleSheet.spreadsheetUrl',
            null
          ),
          importStatus: _.get(
            userData,
            'internalCodeGoogleSheet.importStatus',
            null
          ),
          isLock: _.get(userData, 'internalCodeGoogleSheet.isLock', true)
        },
        additionalPhoneNumber: _.get(userData, 'additionalPhoneNumber', null),
        additionalEmails: _.get(userData, 'additionalEmails', null)
      };
      return user;
    } catch (err) {
      console.log('ERROR', err);
      if (err?.response?.data.statusCode !== 401) {
        dispatch(
          setShowAlert({
            show: true,
            message: err?.response?.data?.msg || err?.response?.data?.message,
            type: 'error'
          })
        );
      }
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const updateRoleAsync = createAsyncThunk(
  'user/updateRole',
  async (payload: UpdateRole, { dispatch, rejectWithValue }) => {
    try {
      const updateRolePayload = _.omit(payload, ['permissions', 'accessToken']);
      const res = await axiosInstance.patch('role', updateRolePayload, {
        headers: {
          Authorization: `Bearer ${payload.accessToken}`
        }
      });
      dispatch(
        setShowAlert({
          show: true,
          message: res.data.msg,
          type: TOAST_TYPE.SUCCESS
        })
      );
      return updateRolePayload;
    } catch (err) {
      if (err?.response?.data.statusCode !== 401) {
        dispatch(
          setShowAlert({
            show: true,
            message: err?.response?.data?.msg || err?.response?.data?.message,
            type: 'error'
          })
        );
      }
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const updatePermissionAsync = createAsyncThunk(
  'user/updatePermission',
  async (payload: UpdatePermission, { dispatch, rejectWithValue }) => {
    try {
      const updatePermissionPayload = _.omit(payload, [
        'permissions',
        'accessToken'
      ]);
      const res = await axiosInstance.patch(
        'permission',
        updatePermissionPayload,
        {
          headers: {
            Authorization: `Bearer ${payload.accessToken}`
          }
        }
      );
      dispatch(
        setShowAlert({
          show: true,
          message: res.data.msg,
          type: TOAST_TYPE.SUCCESS
        })
      );
      return updatePermissionPayload;
    } catch (err) {
      if (err?.response?.data.statusCode !== 401) {
        dispatch(
          setShowAlert({
            show: true,
            message: err?.response?.data?.msg || err?.response?.data?.message,
            type: 'error'
          })
        );
      }
      return rejectWithValue(err?.response?.data);
    }
  }
);
export const updateUserByAdminAsync = createAsyncThunk(
  'user/admin/updateUser',
  async (payload: any, { dispatch, rejectWithValue, getState }) => {
    try {
      await updateUserByAdmin(payload?.data, payload.accessToken);
      dispatch(
        setShowAlert({
          show: true,
          message: 'User details updated successfully.',
          type: TOAST_TYPE.SUCCESS
        })
      );
    } catch (err) {
      if (err?.response?.data.statusCode !== 401) {
        dispatch(
          setShowAlert({
            show: true,
            message: err?.response?.data?.msg || err?.response?.data?.message,
            type: 'error'
          })
        );
      }
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const updateUserAsync = createAsyncThunk(
  'user/updateUserRole',
  async (
    payload: UpdateUserDataParamsType,
    { dispatch, rejectWithValue, getState }
  ) => {
    try {
      const data = _.get(payload, ['data']);
      const updateRes = await axiosInstance.post(
        'user/update',
        { ...data },
        {
          headers: {
            Authorization: `Bearer ${payload.accessToken}`
          }
        }
      );
      // // prevent validate session from calling from router mount & its error msg
      const hasPasswordUpdated = _.get(data, 'currentPassword', false);
      const hasNotificationSettingUpdated = _.get(
        data,
        'notificationSetting',
        false
      );
      const hasAccountActivateToggled = data?.hasOwnProperty('isActive');
      if (hasPasswordUpdated) {
        dispatch(
          setShowAlert({
            show: true,
            message: 'Password updated successfully. Please login again.',
            type: TOAST_TYPE.SUCCESS
          })
        );
        localStorage.clear();
        dispatch(resetAuth());
      } else if (hasNotificationSettingUpdated) {
        const state = getState() as RootState;
        const curremtNotificationSetting = _.get(
          state,
          'user.userData.notificationSetting',
          {}
        );
        const updatedNotificationSetting = _.get(
          payload,
          'data.notificationSetting',
          {}
        );
        const changedKeys: any = getChangedKeys(
          curremtNotificationSetting,
          updatedNotificationSetting
        );
        dispatch(
          setShowAlert({
            show: true,
            message: `${_.capitalize(changedKeys[0])} notification updated!`,
            type: TOAST_TYPE.SUCCESS
          })
        );
        return updatedNotificationSetting;
      } else if (hasAccountActivateToggled) {
        dispatch(
          setShowAlert({
            show: true,
            message: `Account ${
              _.get(data, 'isActive') ? 'reactivated' : 'deactivated'
            } successfully.`,
            type: TOAST_TYPE.SUCCESS
          })
        );
        await dispatch(
          fetchUserDetailsAsync({ accessToken: payload.accessToken })
        );
      } else {
        dispatch(
          setShowAlert({
            show: true,
            message: 'User details updated successfully.',
            type: TOAST_TYPE.SUCCESS
          })
        );
        await dispatch(
          fetchUserDetailsAsync({ accessToken: payload.accessToken })
        );
      }
    } catch (err) {
      if (err?.response?.data.statusCode !== 401) {
        dispatch(
          setShowAlert({
            show: true,
            message: err?.response?.data?.msg || err?.response?.data?.message,
            type: 'error'
          })
        );
      }
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const getUsersBillingAddressAsync = createAsyncThunk(
  'subscription/getUsersCardDetails',
  async (
    _payload: UsersBillingAddressParamsType,
    { rejectWithValue, dispatch }
  ) => {
    try {
      const payload = _.get(_payload, 'data');
      const accessToken = _.get(_payload, 'accessToken');
      const usersCardRes = await axiosInstance.post(
        'user/getBillingAddress',
        payload,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const usersCardData = _.get(usersCardRes, 'data.data');
      const addressInfo = _.get(usersCardData, 'address', {});
      const cardHolderName = _.get(usersCardData, 'name', '');
      const country = _.get(addressInfo, 'country', '');
      const state = _.get(addressInfo, 'state', '');
      const city = _.get(addressInfo, 'city', '');
      const zipcode = _.get(addressInfo, 'postal_code', '');
      const addLine1 = _.get(addressInfo, 'line1', '');
      const addLine2 = _.get(addressInfo, 'line2', '');
      return {
        cardHolderName,
        country,
        state,
        city,
        zipcode,
        addLine1,
        addLine2
      };
    } catch (err) {
      return rejectWithValue(err?.response?.data);
      // dispatch(
      //   setShowAlert({
      //     show: true,
      //     message: err?.response?.data?.msg || err?.response?.data?.message,
      //     type: 'error'
      //   })
      // );
    }
  }
);

export const createTrialUserAsync = createAsyncThunk(
  `${SLICES.USER}/${ACTION_NAMES.CREATE_TRIAL_USER}`,
  createTrialUserService
);

// export const checkPracticeLocationStatusAsync = createAsyncThunk(
//   'auth/practiceLocation',
//   async (
//     { officeId, accessToken }: { officeId: string; accessToken: string },
//     { rejectWithValue, dispatch }
//   ) => {
//     try {
//       const practiceLocationRes = await axiosInstance.get(
//         `practice/location/status/${officeId}`,
//         {
//           headers: {
//             Authorization: `Bearer ${accessToken}`
//           }
//         }
//       );
//       const practiceLocationData = _.get(practiceLocationRes, 'data', {});
//       const statusCode = _.get(practiceLocationData, 'statusCode');
//       const msg = _.get(practiceLocationData, 'msg');
//       if (statusCode === 200) {
//         return true;
//       }
//       dispatch(
//         setShowAlert({
//           show: true,
//           message: msg,
//           type: 'error'
//         })
//       );
//       return false;
//     } catch (err) {
//       dispatch(
//         setShowAlert({
//           show: true,
//           message: err?.response?.data?.msg || err?.response?.data?.message,
//           type: 'error'
//         })
//       );
//       return rejectWithValue(err?.response?.data);
//     }
//   }
// );

export const fetchUsersByConditionAsync = createAsyncThunk(
  'user/fetchUsersByCondition',
  async (payload: FetchUsers, thunkApi) => {
    try {
      const users = await fetchUsersService(payload, thunkApi);
      const usersData = _.get(users, 'data', Array.isArray(users) ? users : []);
      const requestedLimit = _.get(payload, 'filters.limit', defaultPagination.limit);
      const requestedOffset = _.get(payload, 'filters.offset', defaultPagination.offset);
      const requestedSortField = _.get(payload, 'filters.sortBy', '');
      const paginationRaw = _.get(users, 'pagination', {});
      const normalizedLimit =
        Number.isFinite(paginationRaw?.limit) && paginationRaw.limit > 0
          ? Math.floor(paginationRaw.limit)
          : Number.isFinite(requestedLimit) && requestedLimit > 0
            ? Math.floor(requestedLimit)
            : defaultPagination.limit;
      const normalizedOffset =
        Number.isFinite(paginationRaw?.offset) && paginationRaw.offset >= 0
          ? Math.floor(paginationRaw.offset)
          : Number.isFinite(requestedOffset) && requestedOffset >= 0
            ? Math.floor(requestedOffset)
            : defaultPagination.offset;
      const totalItems =
        Number.isFinite(paginationRaw?.totalItems) && paginationRaw.totalItems >= 0
          ? Math.floor(paginationRaw.totalItems)
          : Array.isArray(usersData)
            ? usersData.length
            : 0;
      const totalPages =
        Number.isFinite(paginationRaw?.totalPages) && paginationRaw.totalPages >= 0
          ? Math.floor(paginationRaw.totalPages)
          : Math.ceil(totalItems / normalizedLimit);
      const sortedUsers = requestedSortField
        ? usersData
        : _.orderBy(usersData, ['createdAt'], ['desc']);

      return {
        data: sortedUsers,
        pagination: {
          offset: normalizedOffset,
          limit: normalizedLimit,
          totalItems,
          totalPages,
          hasNextPage:
            typeof paginationRaw?.hasNextPage === 'boolean'
              ? paginationRaw.hasNextPage
              : normalizedOffset + normalizedLimit < totalItems,
          hasPreviousPage:
            typeof paginationRaw?.hasPreviousPage === 'boolean'
              ? paginationRaw.hasPreviousPage
              : normalizedOffset > 0
        } as UserPaginationMeta
      };
    } catch (err) {
      console.log('Error', err);
      return thunkApi.rejectWithValue(err?.message);
    }
  }
);

export const deleteTrialUserAsync = createAsyncThunk(
  `${SLICES.USER}/${ACTION_NAMES.DELETE_TRIAL_USER}`,
  async (payload: DeleteTrialUser, { dispatch, rejectWithValue, getState }) => {
    try {
      const res = await deleteTrialUser(payload, { dispatch });
      const searchedUsers = getUsers(getState() as RootState);
      // // TODO: make generic utility
      // // TODO: add type
      const filteredUsers: any[] = searchedUsers.filter((user: any) => {
        if (user._id !== payload.userId) {
          return true;
        }
      });
      dispatch(
        setShowAlert({
          show: true,
          message: res?.msg,
          type: TOAST_TYPE.SUCCESS
        })
      );
      return filteredUsers;
    } catch (err) {
      console.log('Error', err);
      return rejectWithValue(err?.message);
    }
  }
);

export const updateTrialUserAsync = createAsyncThunk(
  `${SLICES.USER}/${ACTION_NAMES.UPDATE_TRIAL_USER}`,
  async (payload: UpdateTrialUser, { dispatch, rejectWithValue, getState }) => {
    try {
      await updateUser(payload as any, { dispatch });
      dispatch(
        setShowAlert({
          show: true,
          // // TODO: fix msg from backend
          message: 'User details updated successfully.',
          type: TOAST_TYPE.SUCCESS
        })
      );
      return;
    } catch (err) {
      console.log('Error', err);
      return rejectWithValue(err?.message);
    }
  }
);

export const fetchInvoicesByCustomerIdAsync = createAsyncThunk<
  Invoice[],
  GetInvoicesByCustomerIdPayloadType,
  StoreMethods
>(
  `${SLICES.USER}/${ACTION_NAMES.GET_INVOICES_BY_CUST_ID}`,
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const invoicesRes = await getInvoicesByCustomerId(payload, { dispatch });
      const invoicesData = _.get(invoicesRes, 'data', []);
      const modifiedData = invoicesData.map((item: any) => {
        return {
          ...item,
          interval: INVOICES_FREQUENCY[_.get(item, 'interval', 'month')]
        };
      });
      return modifiedData;
    } catch (err) {
      console.log('Error', err);
      return rejectWithValue(err?.message);
    }
  }
);

export const fetchNextInvoiceDateByCustomerIdAsync = createAsyncThunk<
  string,
  GetInvoicesByCustomerIdPayloadType,
  StoreMethods
>(
  `${SLICES.USER}/${ACTION_NAMES.GET_NEXT_INVOICE_DATE_BY_CUST_ID}`,
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const nextInvoiceDateRes = await getNextInvoiceDateByCustomerId(payload, {
        dispatch
      });
      const nextInvoiceDate = _.get(
        nextInvoiceDateRes,
        'data.nextInvoiceDate',
        null
      );
      return nextInvoiceDate;
    } catch (err) {
      console.log('Error', err);
      return rejectWithValue(err?.message);
    }
  }
);

export const updateEmailAndPhoneNumber = createAsyncThunk(
  `${SLICES.USER}/${ACTION_NAMES.UPDATE_EMAIL_AND_PHONE_NUMBER}`,
  async (payload: any, { dispatch, rejectWithValue }) => {
    try {
      const updateUserRes = await updateUser(payload, { dispatch });
      const updateUserData = _.get(updateUserRes, 'data', {});
      const isEmailOtpVerified = _.get(
        updateUserData,
        'data.isEmailOptVerified',
        null
      );
      const isPhoneNumberOtpVerified = _.get(
        updateUserData,
        'data.isMobileOptVerified',
        null
      );
      if (isEmailOtpVerified || isPhoneNumberOtpVerified) {
        dispatch(
          setShowAlert({
            show: true,
            message: isEmailOtpVerified
              ? TOAST_MSGS.UPDATE_EMAIL
              : isPhoneNumberOtpVerified
              ? TOAST_MSGS.UPDATE_PHN_NUM
              : '',
            type: TOAST_TYPE.SUCCESS
          })
        );
      }
      return updateUserData;
    } catch (err) {
      console.log('Error', err);
      return rejectWithValue(err?.message);
    }
  }
);

export const deleteUsersAllActiveSessions = createAsyncThunk(
  `${SLICES.USER}/${ACTION_NAMES.DELETE_ALL_ACTIVE_SESSION}`,
  async (payload: AccessToken, { dispatch, rejectWithValue }) => {
    try {
      await deleteUsersActiveSession(payload, {
        dispatch
      });
    } catch (err) {
      console.log('Error', err);
      return rejectWithValue(err?.message);
    }
  }
);

export const updatePracticeAsync = createAsyncThunk<
  // // TODO: mismatch is keys names in store & payload object of practiceData
  PracticeData,
  Omit<UpdatePracticeDetailsPayloadType, 'data'> & {
    data: Partial<UpdatePracticeDetailsPayloadType['data']>;
  },
  StoreMethods
>(
  `${SLICES.USER}/${ACTION_NAMES.UPDATE_PRACTICE_DETAILS}`,
  async (payload, { getState, dispatch, rejectWithValue }) => {
    try {
      const practiceRes = await updatePractice(payload, {
        dispatch
      });
      const practiceData = _.get(practiceRes, 'data', {});
      const statusCode = _.get(practiceData, 'statusCode', null);
      if (statusCode === 200) {
        dispatch(
          setShowAlert({
            show: true,
            message: 'Location Information successfully updated.',
            type: TOAST_TYPE.SUCCESS
          })
        );
      }
      const state = getState();
      const currentPractice = state.user.userData.practice;

      // // handled mismatch explicitly
      const {
        address1,
        address2,
        zipCode,
        officePhoneNumber,
        ...commonFields
      } = payload?.data || {};

      return {
        ...currentPractice, // Preserve existing fields
        ...commonFields,
        zipcode: zipCode || currentPractice.zipcode,
        addLine1: address1 || currentPractice.addLine1,
        addLine2: address2 || currentPractice.addLine2,
        suite: payload?.data?.suite ?? currentPractice.suite,
        officePhoneNumber: {
          ...currentPractice.officePhoneNumber,
          ...(officePhoneNumber || {})
        }
      } as PracticeData;
    } catch (err) {
      console.log('Error', err);
      if (err?.response?.data.statusCode !== 401) {
        dispatch(
          setShowAlert({
            show: true,
            message: err?.message,
            type: 'error'
          })
        );
      }
      return rejectWithValue(err?.message);
    }
  }
);

export const fetchPracticeByIdAsync = createAsyncThunk<
  Partial<PracticeData>,
  FetchPracticeById,
  StoreMethods
>(
  `${SLICES.USER}/${ACTION_NAMES.FETCH_PRACTICE_DETAILS_BY_ID}`,
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const practiceRes = await fetchPracticeDetailsId(payload, {
        dispatch
      });
      const practiceData = _.get(practiceRes, 'data', {});
      const statusCode = _.get(practiceRes, 'statusCode', null);

      if (statusCode === 200) {
        return {
          name: _.get(practiceData, 'name'),
          state: _.get(practiceData, 'state'),
          city: _.get(practiceData, 'city'),
          zipcode: _.get(practiceData, 'zipCode'),
          addLine1: _.get(practiceData, 'address1'),
          addLine2: _.get(practiceData, 'address2'),
          suite: _.get(practiceData, 'suite', ''),
          officePhoneNumber: {
            countryCode: _.get(practiceData, 'officePhoneNumber.countryCode'),
            number: _.get(practiceData, 'officePhoneNumber.number')
          },
          practiceCommonEmail: _.get(practiceData, 'practiceCommonEmail'),
          sendReportToCommonEmail: _.get(
            practiceData,
            'userSetting.sendReportToCommonEmail'
          )
        };
      }
      return {};
    } catch (err) {
      console.log('Error', err);
      return rejectWithValue(err?.message);
    }
  }
);

export const importPlansFromSheetAsync = createAsyncThunk<
  void,
  importPlansFromSheetPayloadType,
  StoreMethods
>(
  `${SLICES.USER}/${ACTION_NAMES.IMPORT_CDT_CODES_PLAN}`,
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const plansRes = await importPlans(payload, { dispatch });
      const statusCode = _.get(plansRes, 'statusCode', null);
      const msg = _.get(plansRes, 'msg', '');
      if (statusCode === 200) {
        dispatch(
          setShowAlert({
            show: true,
            message: msg,
            type: TOAST_TYPE.SUCCESS
          })
        );
      }
    } catch (err) {
      console.log('Error', err);
      return rejectWithValue(err?.message);
    }
  }
);

export const importInternalPlansAsync = createAsyncThunk<
  void,
  ImportInternalPlansPayloadType,
  StoreMethods
>(
  `${SLICES.USER}/${ACTION_NAMES.IMPORT_INTERNAL_PLANS}`,
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const plansRes = await importInternalPlans(payload);
      const statusCode = _.get(plansRes, 'statusCode', null);
      const msg = _.get(plansRes, 'msg', '');
      if (statusCode === 200) {
        dispatch(
          setShowAlert({
            show: true,
            message: msg,
            type: TOAST_TYPE.SUCCESS
          })
        );
      }
    } catch (err) {
      console.log('Error', err);
      return rejectWithValue(err?.message);
    }
  }
);

export const enablePracticeLocationAsync = createAsyncThunk<
  void,
  EnablePracticeLocationPayloadType,
  StoreMethods
>(
  `${SLICES.USER}/${ACTION_NAMES.ENABLE_PRACTICE_LOCATION}`,
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      await enablePracticeLocation(payload, { dispatch });
    } catch (err) {
      if (err?.response?.data.statusCode !== 401) {
        dispatch(
          setShowAlert({
            show: true,
            message: err?.response?.data?.msg || err?.response?.data?.message,
            type: 'error'
          })
        );
      }
      return rejectWithValue(err?.message);
    }
  }
);

export const toggleIsSheetLockAsync = createAsyncThunk<
  void,
  ToggleIsSheetLockPayloadType,
  StoreMethods
>(
  `${SLICES.USER}/${ACTION_NAMES.TOGGLE_IS_SHEET_LOCK}`,
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      await toggleIsSheetLock(payload, { dispatch });
    } catch (err) {
      return rejectWithValue(err?.message);
    }
  }
);

export const {
  setSelectedUser,
  resetUser,
  setLoader,
  setPagination,
  resetSearchedUsers,
  updateUserData,
  setSortBy,
  setSortDirection,
  setSortField,
  setSearchedUsers
} = userSlice.actions;
export default userSlice.reducer;

export const getAccessToken = (state: RootState) =>
  state?.auth?.tokens?.accessToken;

export const getSikkaToken = (state: RootState) =>
  state?.auth?.tokens?.sikkaToken;

export const getLoading =
  (sliceName: keyof RootState) => (state: RootState) => {
    const slice = state[sliceName];
    return slice && 'loading' in slice ? slice.loading : false;
  };

export const getLimit = (sliceName: keyof RootState) => (state: RootState) => {
  const slice = state[sliceName];
  return slice && 'limit' in slice ? slice.limit : 10;
};

export const getOffset = (sliceName: keyof RootState) => (state: RootState) => {
  const slice = state[sliceName];
  return slice && 'offset' in slice ? slice.offset : 0;
};

export const getSortBy = (sliceName: keyof RootState) => (state: RootState) => {
  const slice = state[sliceName];
  return slice && 'sortBy' in slice ? slice.sortBy : '';
};

export const getSortField =
  (sliceName: keyof RootState) => (state: RootState) => {
    const slice = state[sliceName];
    return slice && 'sortField' in slice ? slice.sortField : '';
  };

export const getSortDirection =
  (sliceName: keyof RootState) =>
  (state: RootState): 'asc' | 'desc' => {
    const slice = state[sliceName];
    return slice && 'sortDirection' in slice
      ? (slice.sortDirection as 'asc' | 'desc')
      : 'asc';
  };

export const getUsers = (state: RootState) => state.user.searchedUsers;
export const getUsersPaginationMeta = (state: RootState) => state.user.pagination;

export const getSelectedUser = (state: RootState) => state.user.selectedUser;

export const getSelectedUserShippingDetails = (state: RootState) =>
  state?.user?.selectedUser?.shippingDetails;

export const getUserRole = (state: RootState) => state.user.userData.role;

export const getAllRoles = (state: RootState) => state.user.roles;

export const getLoggedInUserId = (state: RootState) =>
  state.user.userData.userId;

export const getLoggedInUsersSubscription = (state: RootState) =>
  state.user.userData.subscription;

export const getPracticeLoaction = (state: RootState) =>
  state.user.userData.practiceLocationEnabled;

export const getLoggedInUserData = (state: RootState) => state.user.userData;

export const getLoggedInUsersInvoives = (state: RootState) =>
  state.user.userData.invoices;

export const getCustomerId = (state: RootState) =>
  state?.user?.userData?.payment?.customerId;

export const getNextInvoiceDate = (state: RootState) =>
  state?.user?.userData?.subscription?.nextInvoiceDate;

export const getGoogleSheetData = (state: RootState) =>
  state?.user?.userData?.googleSheet;

export const getSelectedUserGoogleSheetData = (state: RootState) =>
  state?.user?.selectedUser?.googleSheet;

export const getInternalCodesSheetData = (state: RootState) =>
  state?.user?.userData?.internalCodeGoogleSheet;

export const getSelectedUserInternalCodeSheetData = (state: RootState) =>
  state?.user?.selectedUser?.internalCodeGoogleSheet;

export const getSelectedUserPracticeName = (state: RootState) =>
  state?.user?.selectedUser?.practice?.name;

export const getOfficeId = (state: RootState) =>
  state?.user?.userData?.officeId;
